import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

const IndexCard = ({ k, name, formula, meaning, stats, png, bbox, setOverlay, activeOverlayKey }) => (
  <div className={`border rounded-md p-3 ${activeOverlayKey === k ? "border-primary bg-primary/5" : "border-border"}`} data-testid={`index-card-${k}`}>
    <div className="flex gap-3">
      <img src={png} alt={name} className="w-14 h-14 rounded object-cover border border-border shrink-0" style={{ imageRendering: "pixelated" }} />
      <div className="flex-1 min-w-0">
        <div className="font-display font-semibold text-sm">{name}</div>
        {formula && <div className="font-mono text-[10px] text-muted-foreground mt-0.5">{formula}</div>}
        {stats && <div className="font-mono text-[10px] mt-1">μ {stats.mean} · p90 {stats.p90} · valid {stats.valid_pct}%</div>}
      </div>
    </div>
    {meaning && <p className="text-[11px] text-muted-foreground mt-2 leading-snug">{meaning}</p>}
    <Button size="sm" variant={activeOverlayKey === k ? "secondary" : "outline"} className="w-full mt-2 h-8"
      onClick={() => setOverlay(activeOverlayKey === k ? null : { key: k, png, bbox })} data-testid={`show-map-${k}`}>
      {activeOverlayKey === k ? "Hide from map" : "Show on map"}
    </Button>
  </div>
);

export default function IndicesPanel({ project, catalog, setOverlay, activeOverlayKey }) {
  const layers = project.layers || {};
  const prosp = project.prospectivity;
  const meta = {};
  (catalog?.spectral_indices || []).forEach((i) => { meta[i.key] = i; });

  if (Object.keys(layers).length === 0)
    return <p className="text-sm text-muted-foreground">Run pre-processing to generate mineral indices from the selected scene.</p>;

  const bbox = project.boundary_metrics?.bbox;

  return (
    <div className="space-y-3">
      {prosp && (
        <div className={`border rounded-md p-3 ${activeOverlayKey === "prospectivity" ? "border-primary bg-primary/5" : "border-priority-high/40"}`} data-testid="prospectivity-card">
          <div className="flex gap-3">
            <img src={prosp.png} alt="prospectivity" className="w-14 h-14 rounded object-cover border border-border shrink-0" style={{ imageRendering: "pixelated" }} />
            <div className="flex-1">
              <div className="flex items-center gap-2"><span className="font-display font-semibold text-sm">Prospectivity Model</span><Badge className="bg-priority-high text-white text-[9px]">AI Overlay</Badge></div>
              <div className="font-mono text-[10px] text-muted-foreground mt-0.5">Weighted overlay · {project.commodity}</div>
              <div className="font-mono text-[10px] mt-1">veg-masked {prosp.veg_masked_pct}% · water-masked {prosp.water_masked_pct}%</div>
            </div>
          </div>
          <Button size="sm" variant={activeOverlayKey === "prospectivity" ? "secondary" : "default"} className="w-full mt-2 h-8"
            onClick={() => setOverlay(activeOverlayKey === "prospectivity" ? null : { key: "prospectivity", png: prosp.png, bbox: project.boundary_metrics?.bbox })} data-testid="show-map-prospectivity">
            {activeOverlayKey === "prospectivity" ? "Hide from map" : "Show on map"}
          </Button>
        </div>
      )}
      {Object.entries(layers).map(([k, v]) => (
        <IndexCard key={k} k={k} name={meta[k]?.name || k} formula={meta[k]?.formula}
          meaning={meta[k]?.meaning} stats={v.stats} png={v.png}
          bbox={bbox} setOverlay={setOverlay} activeOverlayKey={activeOverlayKey} />
      ))}
    </div>
  );
}
